import { FC } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

import { ArrowIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { PaginationController } from '../../../../components/pagination-controller/index';
import { ScenarioHooks, useFakePagination, usePagination } from '../../../../hooks/index';
import { PageRoute } from '../../../../types/page.types';
import {
  ScenarioCapTableByStakeholder,
  ScenarioType,
  ScenarioTypeTitle,
} from '../../../../types/scenario-modeling.types';
import { StakeholderItem } from './stakeholder-item';

export const StakeholderDetailsPage: FC = () => {
  const { name, id } = useParams<{ id: string; name: string }>();
  const location = useLocation();
  const stakeholders: ScenarioCapTableByStakeholder[] = location.state?.stakeholders;
  const { scenario } = ScenarioHooks.useScenario({ scenarioId: id || '' });
  const navigate = useNavigate();

  const [currentPage, setCurrentPage] = usePagination('scenario-stakeholder-details');
  const { data, totalPages } = useFakePagination({
    currentPage,
    take: 10,
    data: stakeholders || [],
  });

  return (
    <div className="flex w-full flex-col gap-4 rounded-md bg-white px-3 py-4 shadow-sm">
      <div className="flex cursor-pointer gap-2 pt-4 text-xs font-[450] text-gray-400">
        <span className="text-brand-700" onClick={() => navigate(PageRoute.SCENARIO_MODELING)}>
          <AppFormattedMessage id="MY_SCENARIOS" />
        </span>
        <span>/</span>
        <span
          className="text-brand-700"
          onClick={() => navigate(`${PageRoute.SCENARIO_MODELING}/${id}`)}
        >
          {scenario?.name}
        </span>
        <span>/</span>
        <span>{name}</span>
      </div>
      <div className="flex flex-col gap-4 border-y border-gray-100 py-3">
        <div className="flex items-center gap-2">
          <Button
            className="centered size-9 rounded-full"
            onClick={() => navigate(`${PageRoute.SCENARIO_MODELING}/${id}`)}
            style={{
              boxShadow: '0px 0px 24px 0px #00000014',
              border: '1px solid #F2F2F2',
            }}
            styleType="NONE"
          >
            <ArrowIcon />
          </Button>
          <div className="flex flex-col">
            <span className="text-base font-semibold text-gray-700">{name}</span>
            <span className="text-xs font-[450] text-gray-400">
              <AppFormattedMessage id="DETAILS" />
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div
            className={twMerge(
              'rounded-[4px] border-[1px] px-3 py-2 text-sm font-[450]',
              scenario?.type === ScenarioType.FUNDRAISING
                ? 'border-forest-50 bg-forest-25 text-forest-600'
                : 'border-sunbeam-50 bg-sunbeam-50 text-[#C56A08]',
            )}
          >
            <AppFormattedMessage
              id={scenario?.type ? ScenarioTypeTitle[scenario?.type] : 'FUNDRAISING_ROUND'}
            />
          </div>
          <div className="rounded-[4px] border-[1px] border-gray-50 bg-gray-50 px-3 py-2 text-sm font-[450] text-gray-700">
            {scenario?.currentValuation ? (
              <AppFormattedMessage id="LATEST_CAP_TABLE" />
            ) : (
              scenario?.valuation?.name
            )}
          </div>
        </div>
      </div>

      {data?.map((stakeholder) => (
        <StakeholderItem item={stakeholder} key={stakeholder.id} />
      ))}
      <PaginationController
        currentPage={currentPage}
        onClick={setCurrentPage}
        totalPages={totalPages}
      />
    </div>
  );
};
